import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Save, Send, Eye, Upload, X, Paperclip, ExternalLink } from 'lucide-react';
import { SUBJECT_OPTIONS, CLIENT_TYPES, MATURITY_OPTIONS, SUBJECT_FIELDS } from '../lib/subjectFields';
import { useAuth } from '../contexts/AuthContext';

const STEPS = ['Informations generales', 'Details du projet', 'Partenaires & Envoi'];
const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25 Mo

function buildBeevUrl(form) {
  const base = "https://share.hsforms.com/1xigeGQyYREWw2TFAzqytEw3sydz";
  const params = new URLSearchParams();
  if (form.client_name) params.set("company", form.client_name);
  if (form.contact_name) {
    const parts = form.contact_name.split(" ");
    if (parts.length > 1) { params.set("firstname", parts[0]); params.set("lastname", parts.slice(1).join(" ")); }
    else { params.set("lastname", form.contact_name); }
  }
  if (form.email) params.set("email", form.email);
  if (form.phone) params.set("phone", form.phone);
  if (form.address) params.set("address", form.address);
  if (form.postal_code) params.set("code_postal", form.postal_code);
  if (form.city) params.set("city", form.city);
  const comments = [form.comments || '', `Sujet: ${form.subject}`, Object.entries(form.specific_fields || {}).filter(([,v]) => v).map(([k,v]) => `${k}: ${v}`).join(', ')].filter(Boolean).join(' | ');
  if (comments) params.set("commentaires___precisions_sur_le_projet", comments);
  if (form.origin) params.set("non_conseiller_pde", form.origin);
  return `${base}?${params.toString()}`;
}

export default function NewLeadPage() {
  const { user } = useAuth();
  const { id: editId } = useParams(); // Get lead ID if in edit mode
  const isEditMode = !!editId;
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(isEditMode);
  const [form, setForm] = useState({
    client_name: '', siren: '', site_name: '', address: '', postal_code: '', city: '',
    contact_name: '', contact_function: '', phone: '', email: '', client_type: '',
    lead_source: '', origin: '', licensee_name: '', subject: '', sub_subject: '', maturity: '',
    desired_delay: '', comments: '', status: 'brouillon', specific_fields: {},
    attachments: []
  });
  const [partners, setPartners] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [emailPreview, setEmailPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  // Load partners and existing lead data if editing
  useEffect(() => {
    const loadData = async () => {
      try {
        const partnersRes = await API.get('/partners', { params: { active_only: true } });
        setPartners(partnersRes.data);
        
        if (isEditMode) {
          const leadRes = await API.get(`/leads/${editId}`);
          const lead = leadRes.data;
          setForm({
            client_name: lead.client_name || '',
            siren: lead.siren || '',
            site_name: lead.site_name || '',
            address: lead.address || '',
            postal_code: lead.postal_code || '',
            city: lead.city || '',
            contact_name: lead.contact_name || '',
            contact_function: lead.contact_function || '',
            phone: lead.phone || '',
            email: lead.email || '',
            client_type: lead.client_type || '',
            lead_source: lead.lead_source || lead.origin || '',
            origin: lead.origin || '',
            licensee_name: lead.licensee_name || '',
            subject: lead.subject || '',
            sub_subject: lead.sub_subject || '',
            maturity: lead.maturity || '',
            desired_delay: lead.desired_delay || '',
            comments: lead.comments || '',
            status: lead.status || 'brouillon',
            specific_fields: lead.specific_fields || {},
            attachments: lead.attachments || []
          });
          setLoading(false);
        } else if (user?.email) {
          // Pre-fill origin with user email only for new leads
          setForm(prev => ({ ...prev, origin: user.email }));
        }
      } catch (err) {
        toast.error('Erreur lors du chargement');
        if (isEditMode) navigate('/leads');
      }
    };
    loadData();
  }, [editId, isEditMode, user, navigate]);

  const totalAttachmentSize = form.attachments.reduce((sum, a) => sum + (a.size || 0), 0);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (totalAttachmentSize + file.size > MAX_TOTAL_SIZE) {
        toast.error(`Limite de 25 Mo atteinte. Le fichier "${file.name}" ne peut pas etre ajoute.`);
        break;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setForm(prev => ({
          ...prev,
          attachments: [...prev.attachments, { ...res.data }]
        }));
      } catch { toast.error(`Erreur upload: ${file.name}`); }
      finally { setUploading(false); }
    }
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    setForm(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== idx) }));
  };

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const setSpecific = (field, value) => setForm(prev => ({
    ...prev, specific_fields: { ...prev.specific_fields, [field]: value }
  }));

  const filteredPartners = partners.filter(p =>
    !form.subject || p.categories.includes(form.subject)
  );

  const togglePartner = (id) => {
    setSelectedPartners(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSave = async (asStatus) => {
    setSaving(true);
    try {
      const payload = { ...form, status: asStatus || form.status };
      let leadId;
      
      if (isEditMode) {
        // Update existing lead
        await API.put(`/leads/${editId}`, payload);
        leadId = editId;
        toast.success('Lead mis a jour');
      } else {
        // Create new lead
        const res = await API.post('/leads', payload);
        leadId = res.data.id;
        toast.success('Lead enregistre');
      }
      navigate(`/leads/${leadId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    // Check CEE blocking rule
    if (form.subject === 'CEE' && form.specific_fields?.works_signed === 'Oui') {
      toast.error('Impossible d\'envoyer : le devis est deja signe, le projet est irrecevable pour les CEE.');
      return;
    }
    setSending(true);
    try {
      let leadId;
      
      if (isEditMode) {
        // Update existing lead first
        await API.put(`/leads/${editId}`, { ...form, status: 'pret_a_envoyer' });
        leadId = editId;
      } else {
        // Create new lead
        const leadRes = await API.post('/leads', { ...form, status: 'pret_a_envoyer' });
        leadId = leadRes.data.id;
      }
      
      const genRes = await API.post('/emails/generate', { lead_id: leadId });
      await API.post('/emails/send', {
        lead_id: leadId,
        partner_ids: selectedPartners,
        subject: genRes.data.subject,
        body: genRes.data.body
      });
      toast.success('Email(s) envoye(s)');
      navigate(`/leads/${leadId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur d\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handlePreview = async () => {
    try {
      const leadRes = await API.post('/leads', { ...form, status: 'brouillon' });
      const genRes = await API.post('/emails/generate', { lead_id: leadRes.data.id });
      setEmailPreview(genRes.data);
      setShowPreview(true);
      setForm(prev => ({ ...prev, _savedId: leadRes.data.id }));
    } catch (err) {
      toast.error('Erreur de generation');
    }
  };

  const subjectConfig = SUBJECT_FIELDS[form.subject];

  const renderField = (field) => {
    const val = form.specific_fields[field.name] || '';
    // Blocking validation (e.g., CEE devis signé)
    const isBlocked = field.blocking_value && val === field.blocking_value;
    if (field.type === 'multiselect') {
      const selected = form.specific_fields[field.name] ? form.specific_fields[field.name].split(', ').filter(Boolean) : [];
      return (
        <div key={field.name} className="space-y-1.5">
          <div className="flex flex-wrap gap-2 p-2 border bg-white min-h-[40px]" data-testid={`field-${field.name}`}>
            {field.options.map(opt => (
              <label key={opt} className="flex items-center gap-1.5 cursor-pointer text-xs">
                <Checkbox checked={selected.includes(opt)}
                  onCheckedChange={(checked) => {
                    const newSel = checked ? [...selected, opt] : selected.filter(s => s !== opt);
                    setSpecific(field.name, newSel.join(', '));
                  }} />
                {opt}
              </label>
            ))}
          </div>
        </div>
      );
    }
    if (field.type === 'select') {
      return (
        <div key={field.name}>
          <Select value={val} onValueChange={v => setSpecific(field.name, v)}>
            <SelectTrigger className="h-9 text-sm" data-testid={`field-${field.name}`}>
              <SelectValue placeholder="Selectionner..." />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
          {isBlocked && (
            <div className="mt-2 p-3 bg-red-50 border border-red-300 text-red-700 text-xs font-medium" data-testid={`blocking-${field.name}`}>
              {field.blocking_message}
            </div>
          )}
        </div>
      );
    }
    if (field.type === 'textarea') {
      return <Textarea value={val} onChange={e => setSpecific(field.name, e.target.value)}
        className="text-sm" data-testid={`field-${field.name}`} key={field.name} />;
    }
    return <Input type={field.type || 'text'} value={val} onChange={e => setSpecific(field.name, e.target.value)}
      className="h-9 text-sm" data-testid={`field-${field.name}`} key={field.name} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent animate-spin" style={{borderRadius:'50%'}} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 fade-in" data-testid="new-lead-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-bold">{isEditMode ? 'Modifier le lead' : 'Nouveau lead'}</h1>
          <p className="text-sm text-muted-foreground">Etape {step + 1} / {STEPS.length} - {STEPS[step]}</p>
        </div>
      </div>

      {/* Progress */}
      <div className="flex gap-1">
        {STEPS.map((s, i) => (
          <div key={i} className={`h-1 flex-1 transition-colors ${i <= step ? 'bg-[#397DFF]' : 'bg-slate-200'}`} />
        ))}
      </div>

      {/* Step 1: General Info */}
      {step === 0 && (
        <Card className="border shadow-sm">
          <CardHeader><CardTitle className="text-base font-heading">Informations client</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Nom du client / societe *</Label>
                <Input value={form.client_name} onChange={e => set('client_name', e.target.value)}
                  className="h-9 text-sm" data-testid="field-client_name" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">SIREN</Label>
                <Input value={form.siren} onChange={e => set('siren', e.target.value)}
                  className="h-9 text-sm" data-testid="field-siren" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Adresse du site *</Label>
                <Input value={form.address} onChange={e => set('address', e.target.value)}
                  className="h-9 text-sm" data-testid="field-address" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Code postal</Label>
                  <Input value={form.postal_code} onChange={e => set('postal_code', e.target.value)}
                    className="h-9 text-sm" data-testid="field-postal_code" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Ville *</Label>
                  <Input value={form.city} onChange={e => set('city', e.target.value)}
                    className="h-9 text-sm" data-testid="field-city" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Interlocuteur principal</Label>
                <Input value={form.contact_name} onChange={e => set('contact_name', e.target.value)}
                  className="h-9 text-sm" data-testid="field-contact_name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Fonction</Label>
                <Input value={form.contact_function} onChange={e => set('contact_function', e.target.value)}
                  className="h-9 text-sm" data-testid="field-contact_function" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Telephone</Label>
                <Input value={form.phone} onChange={e => set('phone', e.target.value)}
                  className="h-9 text-sm" data-testid="field-phone" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Email</Label>
                <Input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                  className="h-9 text-sm" data-testid="field-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Type de client</Label>
                <Select value={form.client_type} onValueChange={v => set('client_type', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="field-client_type"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                  <SelectContent>{CLIENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Origine de la demande (apporteur d'affaires)</Label>
                <Input value={form.lead_source} onChange={e => set('lead_source', e.target.value)}
                  placeholder="Ex: Julien FLAHAUT, Selectra, Partenaire..."
                  className="h-9 text-sm" data-testid="field-lead_source" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Email du collaborateur PDE (sera en copie des emails)</Label>
                <Input type="email" value={form.origin} onChange={e => set('origin', e.target.value)}
                  placeholder="prenom.nom@placedesenergies.com"
                  className="h-9 text-sm" data-testid="field-origin" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Sujet principal *</Label>
                <Select value={form.subject} onValueChange={v => { set('subject', v); setForm(prev => ({ ...prev, specific_fields: {} })); }}>
                  <SelectTrigger className="h-9 text-sm" data-testid="field-subject"><SelectValue placeholder="Selectionner le sujet" /></SelectTrigger>
                  <SelectContent>{SUBJECT_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Maturite du projet</Label>
                <Select value={form.maturity} onValueChange={v => set('maturity', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="field-maturity"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                  <SelectContent>{MATURITY_OPTIONS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Delai souhaite</Label>
              <Input value={form.desired_delay} onChange={e => set('desired_delay', e.target.value)}
                className="h-9 text-sm" data-testid="field-desired_delay" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Commentaires</Label>
              <Textarea value={form.comments} onChange={e => set('comments', e.target.value)}
                className="text-sm" rows={3} data-testid="field-comments" />
            </div>
            {/* Pieces jointes */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Pieces jointes</Label>
              <div className="text-[11px] text-muted-foreground mb-1">
                {(totalAttachmentSize / (1024*1024)).toFixed(1)} Mo / 25 Mo utilises
                {totalAttachmentSize > MAX_TOTAL_SIZE * 0.8 && (
                  <span className="text-orange-500 ml-2 font-medium">Attention : proche de la limite</span>
                )}
              </div>
              {form.attachments.length > 0 && (
                <div className="space-y-1">
                  {form.attachments.map((att, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 border text-sm">
                      <Paperclip size={13} className="text-muted-foreground shrink-0" />
                      <span className="flex-1 truncate">{att.original_name}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{((att.size || 0) / 1024).toFixed(0)} Ko</span>
                      <button onClick={() => removeAttachment(idx)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-attachment-${idx}`}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#397DFF] hover:text-[#2563EB] font-medium" data-testid="add-attachment-btn">
                <input type="file" multiple className="hidden" onChange={handleFileUpload} disabled={uploading || totalAttachmentSize >= MAX_TOTAL_SIZE} />
                <Upload size={14} /> {uploading ? 'Upload en cours...' : '+ Ajouter une piece jointe'}
              </label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Subject-specific fields */}
      {step === 1 && (
        <Card className="border shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-heading">
              {subjectConfig ? `Details - ${subjectConfig.label}` : 'Selectionnez un sujet a l\'etape precedente'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {subjectConfig ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {subjectConfig.fields.map(field => (
                    <div key={field.name} className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        {field.label} {field.required && '*'}
                      </Label>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
                {subjectConfig.suggested_attachments.length > 0 && (
                  <div className="mt-4 p-3 bg-accent/50 border border-accent">
                    <p className="text-xs font-semibold text-accent-foreground mb-1">Pieces jointes suggerees :</p>
                    <ul className="text-xs text-muted-foreground space-y-0.5">
                      {subjectConfig.suggested_attachments.map(a => (
                        <li key={a}>- {a}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Veuillez d'abord selectionner un sujet dans l'etape 1
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Partners & Email */}
      {step === 2 && (
        <div className="space-y-4">
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle className="text-base font-heading">Selection des partenaires</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredPartners.length > 0 ? (
                <div className="space-y-2">
                  {filteredPartners.map(p => (
                    <label key={p.id} className="flex items-center gap-3 p-3 border hover:bg-slate-50 transition-colors cursor-pointer"
                      data-testid={`partner-select-${p.id}`}>
                      <Checkbox checked={selectedPartners.includes(p.id)}
                        onCheckedChange={() => togglePartner(p.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.email_primary} - {p.categories.join(', ')}</p>
                      </div>
                      {p.form_url && (
                        <div className="flex flex-col items-end shrink-0 gap-0.5">
                          <a href={p.name === 'Beev' ? buildBeevUrl(form) : p.form_url}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[#397DFF] hover:underline font-medium"
                            onClick={e => e.stopPropagation()}
                            data-testid={`partner-form-link-${p.id}`}>
                            <ExternalLink size={12} /> {p.name === 'Beev' ? 'Remplir le formulaire Beev (pre-rempli)' : 'Formulaire partenaire'}
                          </a>
                          {p.name === 'Beev' && (
                            <span className="text-[10px] text-orange-600 font-medium">
                              Beev necessite de remplir ce formulaire en plus de l'email
                            </span>
                          )}
                        </div>
                      )}
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">Aucun partenaire pour ce sujet</p>
              )}
            </CardContent>
          </Card>

          {showPreview && emailPreview && (
            <Card className="border shadow-sm">
              <CardHeader>
                <CardTitle className="text-base font-heading">Apercu de l'email</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="p-2 bg-slate-50 border">
                    <p className="text-xs text-muted-foreground">Objet :</p>
                    <p className="text-sm font-medium">{emailPreview.subject}</p>
                  </div>
                  <div className="p-3 bg-slate-50 border">
                    <pre className="text-xs whitespace-pre-wrap font-sans">{emailPreview.body}</pre>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex gap-2">
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep(s => s - 1)} data-testid="step-prev-btn">
              <ArrowLeft size={14} className="mr-1" /> Precedent
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleSave('brouillon')} disabled={saving} data-testid="save-draft-btn">
            <Save size={14} className="mr-1" /> {saving ? 'Enregistrement...' : 'Brouillon'}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button className="bg-[#397DFF] hover:bg-[#2563EB] text-white" onClick={() => setStep(s => s + 1)} data-testid="step-next-btn">
              Suivant <ArrowRight size={14} className="ml-1" />
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handlePreview} data-testid="preview-email-btn">
                <Eye size={14} className="mr-1" /> Apercu email
              </Button>
              <Button className="bg-[#397DFF] hover:bg-[#2563EB] text-white"
                onClick={handleSendEmail} disabled={sending || selectedPartners.length === 0}
                data-testid="send-email-btn">
                <Send size={14} className="mr-1" /> {sending ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
